/**
 * image-provenance — in-memory registry of WHERE every image we placed came
 * from. Office.js cannot extract image bytes back out of a shape fill, so the
 * only way "save this deck as a template" can persist images is to remember
 * their source at insert time: durable refs (workspace fileIds, URLs) are
 * stored as references in the deck spec; ephemeral sources (generated /
 * attachment / staged / folder / raw base64) keep their bytes here until
 * upload_template_assets pushes them to durable storage.
 *
 * Session-scoped like generated-images.ts — the deck spec fragment is the
 * persistence, this registry only bridges insert→save within one session.
 */

/** Where an inserted image's bytes came from. */
export type ImageSource =
  | "workspace" // Usable workspace file — durable, restore by fileId
  | "url" // external URL — durable-ish, restore by URL
  | "generated" // AI-generated (IMAGE_GENERATED push) — ephemeral
  | "attachment" // chat paperclip upload — conversation-scoped
  | "staged" // data-staging file — conversation-scoped
  | "folder" // attached local folder — session-scoped
  | "base64"; // raw base64 from the model — ephemeral

export interface ImageProvenance {
  /** Slide index the shape lives on, or "master" for master branding. */
  scope: number | "master";
  shapeId: string;
  source: ImageSource;
  /** fileId / URL / alias / path — whatever addresses the source. */
  ref?: string;
  /** Normalized PNG base64 — kept ONLY for non-durable sources. */
  base64?: string;
}

/** Sources restorable by reference alone — no bytes kept, no upload needed. */
const DURABLE_SOURCES: ReadonlySet<ImageSource> = new Set<ImageSource>(["workspace", "url"]);

const MAX_KEPT = 20;

/** Newest first. */
const entries: ImageProvenance[] = [];

export function recordImageProvenance(entry: ImageProvenance): void {
  if (!entry.shapeId) return;
  const key = (e: ImageProvenance) => `${e.scope}:${e.shapeId}`;
  const existing = entries.findIndex((e) => key(e) === key(entry));
  if (existing !== -1) entries.splice(existing, 1);
  entries.unshift({
    ...entry,
    // Bytes are only worth holding when the ref can't restore the image.
    base64: DURABLE_SOURCES.has(entry.source) ? undefined : entry.base64,
  });
  if (entries.length > MAX_KEPT) entries.length = MAX_KEPT;
}

/** Provenance for one shape, or null when the image predates this session. */
export function getImageProvenance(scope: number | "master", shapeId: string): ImageProvenance | null {
  return entries.find((e) => e.scope === scope && e.shapeId === shapeId) ?? null;
}

/** All recorded provenance, newest first (bytes included where held). */
export function listImageProvenance(): ImageProvenance[] {
  return [...entries];
}

export function isDurableSource(source: ImageSource): boolean {
  return DURABLE_SOURCES.has(source);
}

/** Test-only. */
export function __resetImageProvenanceForTests(): void {
  entries.length = 0;
}
